import {AnamneseActions, AnamneseActionTypes} from './anamnese.actions';
import {createFeatureSelector} from '@ngrx/store';
import { LINGUAL_RETAINER_PRODUCT_KEYS, Product, ProductInfoForKey, ProductKey, PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS, PROFESSIONAL_PRODUCT_KEYS, ToothArea } from "libs/product/src/lib/models/product.model";
import { FunctionALQuestionnaireModel } from 'libs/questionaire/src/lib/functional/models/questionnaire-functional.model';
import { MichiganSplintQuestionnaireModel } from 'libs/questionaire/src/lib/michigan-splint/models/questionnaire-michigan-splint.model';
import { ANAMNESE_ADDITIONAL_UPLOAD_TYPES, AnamneseFunctionALUploadType, AnamneseUploadType } from 'libs/media/src/lib/models/media.model';
import { SapAddress, SapShippingCostItem } from 'libs/sap/src/lib/models/sap.model';
import { TreatmentModel } from 'libs/treatments/src/lib/models/treatment.model';
import { BenefitName } from 'libs/benefits/src/lib/models/benefit.model';

export interface Options {
	uj: ToothArea;
	lj: ToothArea;
}

const initialOptions = {
	uj: undefined,
	lj: undefined,
};

export interface AnamneseFileInfo {
	mediaId: string,
	orgFilename: string
	index?: number, //only necessary for additional files
	functionALType?: AnamneseFunctionALUploadType
}

export interface AnamneseFiles{
	uj: AnamneseFileInfo,
	lj: AnamneseFileInfo,
	additionals: AnamneseFileInfo[]
}

export interface AnamneseFilesFunctionAL{
	jaws: AnamneseFileInfo[]
	extraorals: AnamneseFileInfo[]
	intraorals: AnamneseFileInfo[]
	miscellaneous: AnamneseFileInfo[]
}

export interface AnamneseState {
	patientId: string;
	treatmentId: string;
	product: Product & { handling: {
				shipping?: SapAddress;
				billing?: SapAddress;
				hasUniqueBillingAddress?: boolean;
				hasUniqueShippingAddress?: boolean;
			}
		};
	comment: string;
	questionaire;
	questionaireAnswers;
	questionairePreparedAnswers;
	questionnaireAnswersFunctionAL?: FunctionALQuestionnaireModel;
	questionnaireAnswersMichigan?: MichiganSplintQuestionnaireModel;
	uploadedFiles: AnamneseFiles,
	uploadedFilesFunctionAL: AnamneseFilesFunctionAL,
	shippingCosts: SapShippingCostItem[];
	parentTreatmentId?: string;
	caseAssessmentTreatment?: TreatmentModel;
	discountedMemotainQuota?: {ujAvailable: boolean, ljAvailable: boolean};
	professionalPrintService?: boolean;
	skipDataTransferFlag?: boolean;
	availableBenefits?: BenefitName[];
	selectedBenefitName?: BenefitName;
}

export const initialAnamneseState: AnamneseState = {
	patientId: undefined,
	treatmentId: undefined,
	product: null,
	comment: null,
	questionaire: null,
	questionaireAnswers: null,
	questionairePreparedAnswers: null,
	questionnaireAnswersFunctionAL: undefined,
	questionnaireAnswersMichigan: undefined,
	uploadedFiles: {uj: null, lj: null, additionals: [] },
	uploadedFilesFunctionAL: {
		jaws: [],
		extraorals: [],
		intraorals: [],
		miscellaneous: [],
	 },
	shippingCosts: [],
	caseAssessmentTreatment: undefined,
	parentTreatmentId: undefined,
	discountedMemotainQuota: undefined,
	professionalPrintService: undefined,
	skipDataTransferFlag: undefined,
	availableBenefits: [],
	selectedBenefitName: undefined,
};

export const selectAnamneseState = createFeatureSelector<AnamneseState>('anamnese');

export function reducer(state = initialAnamneseState, action: AnamneseActions): AnamneseState {
	switch (action.type) {
		case AnamneseActionTypes.ClearAnamneseRequested:
			return {...initialAnamneseState, patientId: state.patientId, shippingCosts: state.shippingCosts};

		case AnamneseActionTypes.ToothAreaUpdated: {
			const jaw =
				action.payload.jaw === "uj" ? { uj: action.payload.value } : { lj: action.payload.value };
			return {
				...state,
				product: {
					...state.product,
					options: {
						...state.product.options,
						...jaw,
					},
				},
			};
		}

		case AnamneseActionTypes.JawToggled: {
			//action.payload is either 'uj' or 'lj
			const prod = structuredClone(state.product);

			const isRetainerProduct = LINGUAL_RETAINER_PRODUCT_KEYS.includes(prod.key);
			const isMichiganSplint = prod.key === ProductKey.MICHIGAN_SPLINT;
			const toothArea = ProductInfoForKey[prod.key]?.toothArea as ToothArea;

			//unset selected jaw
			if (prod.options[action.payload]) {
				prod.options[action.payload] = undefined;
				return {
					...state,
					product: prod
				};
			}

			if (isMichiganSplint) {
				if (action.payload === "uj") {
					prod.options.uj = toothArea;
					prod.options.lj = undefined;
				} else {
					prod.options.lj = toothArea;
					prod.options.uj = undefined;
				}
			} else {
				prod.options[action.payload] = isRetainerProduct
					? "-" //placeholder since tooth range is set later
					: toothArea;
			}
			return {
				...state,
				product: prod
			};
		}

		case AnamneseActionTypes.BothJawsUpdated:
			return {
				...state,
				product: {
					...state.product,
					options: {
						...state.product.options,
						uj: ToothArea.SEVEN_SEVEN,
						lj: ToothArea.SEVEN_SEVEN
					}
				}
			};

		case AnamneseActionTypes.ParentTreatmentSelected:
			return {
				...state,
				parentTreatmentId: action.payload.parentTreatmentId
			};

		case AnamneseActionTypes.PatientSelected:
			return {
				...state,
				patientId: action.payload
			};

		case AnamneseActionTypes.ProductSelected: {
			const product: Partial<Product> = {
				key: action.payload.key,
				options: action.payload.options ?? initialOptions,
			};

			return {
				...state,
				product: {
					...state.product,
					...product,
				},
			};
		}

		case AnamneseActionTypes.OptionsUpdated:
			return {
				...state,
				product: {
					...state.product,
					options: {
						...state.product.options,
						...action.payload,
					}
				}
			};

		case AnamneseActionTypes.QuestionaireAnswersUpdated: {
			const answers = { ...action.data };
			if (
				action.productKey === ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT
			) {
				return {
					...state,
					questionnaireAnswersFunctionAL: answers,
				};
			}
			if (
				[...PROFESSIONAL_PRODUCT_KEYS, ...PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS].includes(
					action.productKey
				)
			) {
				return {
					...state,
					questionaireAnswers: answers,
				};
			}
			if (action.productKey === ProductKey.MICHIGAN_SPLINT) {
				return {
					...state,
					questionnaireAnswersMichigan: answers,
				};
			}
			return;
		}

		case AnamneseActionTypes.QuestionairePreparedAnswersUpdated:
			return {
				...state,
				questionairePreparedAnswers: action.payload
			};

		case AnamneseActionTypes.HandlingUpdated: {
			const handling =
				state.product && state.product.handling
					? { ...state.product.handling, ...action.payload }
					: { ...action.payload };
			return {
				...state,
				product: {
					...state.product,
					handling,
				},
			};
		}

		case AnamneseActionTypes.SettingsUpdated:
			return {
				...state,
				...action.payload,
			};

		case AnamneseActionTypes.TreatmentIdUpdated:
			return {
				...state,
				treatmentId: action.payload.treatmentId
			};

		case AnamneseActionTypes.MediaFileUploaded: {
			const updatedAnamneseFiles = structuredClone(state.uploadedFiles);
			const uploadType = action.uploadType;
			const mediaId = action.mediaId;
			const orgFilename = action.orgFilename;
			const index = action.index;

			switch (uploadType) {
				case AnamneseUploadType.UJ:
					updatedAnamneseFiles.uj = { mediaId: mediaId, orgFilename: orgFilename };
					break;
				case AnamneseUploadType.LJ:
					updatedAnamneseFiles.lj = { mediaId: mediaId, orgFilename: orgFilename };
					break;
				default:
					if (ANAMNESE_ADDITIONAL_UPLOAD_TYPES.includes(uploadType)) {
						updatedAnamneseFiles.additionals.push({
							mediaId: mediaId,
							orgFilename: orgFilename,
							index: index,
						});
					}
					break;
			}

			return {
				...state,
				uploadedFiles: updatedAnamneseFiles,
			};
		}

		case AnamneseActionTypes.MediaFileDeleted: {
			const anamneseFiles = structuredClone(state.uploadedFiles);

			switch (action.uploadType) {
				case AnamneseUploadType.UJ:
					anamneseFiles.uj = null;
					break;
				case AnamneseUploadType.LJ:
					anamneseFiles.lj = null;
					break;
				default:
					if (ANAMNESE_ADDITIONAL_UPLOAD_TYPES.includes(action.uploadType)) {
						anamneseFiles.additionals = anamneseFiles.additionals.filter(
							(elem) => elem.index !== action.index
						);
					}
					break;
			}

			return {
				...state,
				uploadedFiles: anamneseFiles,
			};
		}

		case AnamneseActionTypes.MediaFileFunctionALUploaded: {
			const updatedAnamneseFilesFunctionAL = structuredClone(state.uploadedFilesFunctionAL);
			const array = getArrayFromUploadType(updatedAnamneseFilesFunctionAL, action.uploadType);
			array.push({
				mediaId: action.mediaId,
				orgFilename: action.orgFilename,
				functionALType: action.uploadType,
			});
			return {
				...state,
				uploadedFilesFunctionAL: updatedAnamneseFilesFunctionAL,
			};
		}

		case AnamneseActionTypes.MediaFileFunctionALDeleted: {
			const updatedFiles = structuredClone(state.uploadedFilesFunctionAL);
			updatedFiles.jaws = updatedFiles.jaws.filter((elem) => elem.functionALType !== action.uploadType);
			updatedFiles.extraorals = updatedFiles.extraorals.filter(
				(elem) => elem.functionALType !== action.uploadType
			);
			updatedFiles.intraorals = updatedFiles.intraorals.filter(
				(elem) => elem.functionALType !== action.uploadType
			);
			updatedFiles.miscellaneous = updatedFiles.miscellaneous.filter(
				(elem) => elem.functionALType !== action.uploadType
			);
			return {
				...state,
				uploadedFilesFunctionAL: updatedFiles,
			};
		}

		case AnamneseActionTypes.ShippingCostsLoaded:
			return {
				...state,
				shippingCosts: action.payload.shippingCosts
			};

		case AnamneseActionTypes.ClearAnamneseUploadedFilesRequested:
			return {
				...state,
				uploadedFiles: structuredClone(initialAnamneseState.uploadedFiles),
				uploadedFilesFunctionAL: structuredClone(initialAnamneseState.uploadedFilesFunctionAL)
			}

		case AnamneseActionTypes.CaseAssessmentTreatmentSelected: {
			const caseAssessmentData = action.payload.caseAssessmentTreatment.caseAssessmentData;
			const recommendedKey = caseAssessmentData.recommendedKey;
			const toothArea = ProductInfoForKey[recommendedKey].toothArea as ToothArea;
			return {
				...state,
				caseAssessmentTreatment: action.payload.caseAssessmentTreatment,
				product: {
					...state.product,
					options: {
						...state.product.options,
						uj: caseAssessmentData.uj ? toothArea : undefined,
						lj: caseAssessmentData.lj ? toothArea : undefined,
					},
				},
				questionairePreparedAnswers:
					action.payload.caseAssessmentTreatment?.quotation?.anamnese?.questionaireAnswers,
			};
		}
		case AnamneseActionTypes.SetDiscountedMemotainQuota: {
			if (action.payload.ujAvailable && action.payload.ljAvailable) {
				return {
					...state,
					discountedMemotainQuota: action.payload,
				};
			}
			const preSelectedProduct = structuredClone(state.product) as any; //'as any' because we want to set '-' as toothRange
			preSelectedProduct.options.uj = action.payload.ujAvailable ? "-" : undefined; //placeholder since tooth range is set later
			preSelectedProduct.options.lj = action.payload.ljAvailable ? "-" : undefined; //placeholder since tooth range is set later
			return {
				...state,
				product: preSelectedProduct,
				discountedMemotainQuota: action.payload,
			};
		}
		case AnamneseActionTypes.ProfessionalPrintServiceSelected:
			return {
				...state,
				professionalPrintService: action.isSelected,
		    }
		case AnamneseActionTypes.SetSkipDataTransferFlag:
			return {
				...state,
				skipDataTransferFlag: action.payload,
			}

		case AnamneseActionTypes.SetAvailableBenefits:
			return {
				...state,
				availableBenefits: action.benefits,
			}

		case AnamneseActionTypes.SetSelectedBenefitName:
			return {
				...state,
				selectedBenefitName: action.benefitName,
			}
		default:
			return state;
	}
}

function getArrayFromUploadType(uploadedFilesFunctionAL, type: AnamneseFunctionALUploadType) : AnamneseFileInfo[]{
	switch(type){
		case AnamneseFunctionALUploadType.UJ_BITE:
		case AnamneseFunctionALUploadType.LJ_BITE:
		case AnamneseFunctionALUploadType.UJ_REGISTRATION:
		case AnamneseFunctionALUploadType.LJ_REGISTRATION:
			return uploadedFilesFunctionAL.jaws;
		case AnamneseFunctionALUploadType.EXTRAORAL_FRONT_MOUTH_CLOSED:
		case AnamneseFunctionALUploadType.EXTRAORAL_FRONT_MOUTH_OPEN:
		case AnamneseFunctionALUploadType.EXTRAORAL_FRONT_SMILE:
		case AnamneseFunctionALUploadType.EXTRAORAL_RIGHT:
			return uploadedFilesFunctionAL.extraorals;
		case AnamneseFunctionALUploadType.INTRAORAL_FRONT:
		case AnamneseFunctionALUploadType.INTRAORAL_LEFT:
		case AnamneseFunctionALUploadType.INTRAORAL_RIGHT:
		case AnamneseFunctionALUploadType.INTRAORAL_UJ:
		case AnamneseFunctionALUploadType.INTRAORAL_LJ:
			return uploadedFilesFunctionAL.intraorals;
		case AnamneseFunctionALUploadType.MISC_OPG:
		case AnamneseFunctionALUploadType.MISC_FRS:
			return uploadedFilesFunctionAL.miscellaneous;
	}
}


